import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, GeoJSON, TileLayer } from 'react-leaflet';
import { ConfigProvider, DatePicker, Slider, Spin, Typography } from 'antd';
import ruRU from 'antd/locale/ru_RU';
import { useTranslation } from 'react-i18next';
import Papa from 'papaparse';
import dayjs from 'dayjs';
import 'leaflet/dist/leaflet.css';
import classes from './weatherMap.module.css';
import Header from '../components/Header/Header';
import Footer from '../components/Footer/Footer';

const { Text } = Typography;

const MIN_DATE = dayjs('2000-01-01');
const MAX_DATE = dayjs('2025-12-31');
const TOTAL_DAYS = MAX_DATE.diff(MIN_DATE, 'day');

const MONTHS_RU = [
    'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
    'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];
const formatDateRu = (d) => `${d.date()} ${MONTHS_RU[d.month()]} ${d.year()} г.`;

// GeoJSON shapeISO → CSV file name + Russian region name
const REGION_MAP = {
    'KZ-PAV': { csv: 'Павлодарская областьПавлодар.csv',                         name: 'Павлодарская область' },
    'KZ-ZHA': { csv: 'Жамбылская областьТараз.csv',                              name: 'Жамбылская область' },
    'KZ-KUS': { csv: 'Костанайская областьКостанай.csv',                         name: 'Костанайская область' },
    'KZ-MAN': { csv: 'Мангистауская областьАктау.csv',                           name: 'Мангистауская область' },
    'KZ-KAR': { csv: 'Карагандинская областьКараганда.csv',                      name: 'Карагандинская область' },
    'KZ-KZY': { csv: 'Кызылординская областьКызылорда.csv',                      name: 'Кызылординская область' },
    'KZ-VOS': { csv: 'Восточно-Казахстанская областьУсть-Каменогорск.csv',       name: 'Восточно-Казахстанская область' },
    'KZ-AKT': { csv: 'Актюбинская областьАктобе.csv',                            name: 'Актюбинская область' },
    'KZ-ATY': { csv: 'Атырауская областьАтырау.csv',                             name: 'Атырауская область' },
    'KZ-YUZ': { csv: 'Туркестанская областьТуркестан.csv',                       name: 'Туркестанская область' },
    'KZ-AKM': { csv: 'Акмолинская областьКокшетау.csv',                          name: 'Акмолинская область' },
    'KZ-ALM': { csv: 'Алматинская областьКонаев.csv',                            name: 'Алматинская область' },
    'KZ-SEV': { csv: 'Северо-Казахстанская областьПетропавловск.csv',            name: 'Северо-Казахстанская область' },
    'KZ-ALA': { csv: 'г. Алматы.csv',                                            name: 'г. Алматы' },
    'KZ-ZAP': { csv: 'Западно-Казахстанская областьУральск.csv',                 name: 'Западно-Казахстанская область' },
    'KZ-AST': { csv: 'г. Астана.csv',                                            name: 'г. Астана' },
};

// Temperature → hex color (blue–yellow–red diverging scale)
function getTempColor(temp) {
    if (temp == null || isNaN(temp)) return '#b0bec5';
    if (temp < -25) return '#053061';
    if (temp < -15) return '#2166ac';
    if (temp < -5)  return '#74add1';
    if (temp < 5)   return '#abd9e9';
    if (temp < 15)  return '#fee090';
    if (temp < 25)  return '#f46d43';
    return '#a50026';
}

function fmt(val, unit = '') {
    if (val == null || isNaN(val)) return '—';
    return `${val.toFixed(1)}${unit}`;
}

// Parse CSV: first 3 lines are metadata, then headers, then data
function parseWeatherCSV(text) {
    const lines = text.split('\n');
    const dataSection = lines.slice(3).join('\n');
    const { data } = Papa.parse(dataSection, { header: true, skipEmptyLines: true });
    const lookup = {};
    data.forEach(row => {
        const date = (row.time || '').trim();
        if (!date) return;
        lookup[date] = {
            tempMean:           parseFloat(row['temperature_2m_mean (°C)']),
            tempMax:            parseFloat(row['temperature_2m_max (°C)']),
            tempMin:            parseFloat(row['temperature_2m_min (°C)']),
            precipitation:      parseFloat(row['precipitation_sum (mm)']),
            rain:               parseFloat(row['rain_sum (mm)']),
            snowfall:           parseFloat(row['snowfall_sum (cm)']),
            evapotranspiration: parseFloat(row['et0_fao_evapotranspiration (mm)']),
            humidityMean:       parseFloat(row['relative_humidity_2m_mean (%)']),
            humidityMax:        parseFloat(row['relative_humidity_2m_max (%)']),
            humidityMin:        parseFloat(row['relative_humidity_2m_min (%)']),
        };
    });
    return lookup;
}

const NORMAL_STYLE = { weight: 1,   color: '#ffffff', fillOpacity: 0.75, opacity: 1 };
const HOVER_STYLE  = { weight: 2.5, color: '#333333', fillOpacity: 0.92, opacity: 1 };

const buildLegendItems = (t) => [
    ['#053061', '< −25 °C'],
    ['#2166ac', '−25 / −15 °C'],
    ['#74add1', '−15 / −5 °C'],
    ['#abd9e9', '−5 / +5 °C'],
    ['#fee090', '+5 / +15 °C'],
    ['#f46d43', '+15 / +25 °C'],
    ['#a50026', '> +25 °C'],
    ['#b0bec5', t('weatherMap.noData')],
];

const SLIDER_MARKS = Object.fromEntries(
    [2000, 2005, 2010, 2015, 2020, 2025].map(y => [
        dayjs(`${y}-01-01`).diff(MIN_DATE, 'day'),
        String(y),
    ])
);

export default function WeatherMapPage() {
    const { t } = useTranslation();
    const legendItems = buildLegendItems(t);
    const [selectedDate, setSelectedDate] = useState(dayjs('2024-07-15'));
    const [geoData,      setGeoData]      = useState(null);
    const [weatherData,  setWeatherData]  = useState({});
    const [loading,      setLoading]      = useState(true);
    const [tooltip,      setTooltip]      = useState(null); // { x, y, iso }

    // Refs to avoid stale closures in Leaflet event handlers
    const layerRefs       = useRef({});
    const weatherDataRef  = useRef({});
    const selectedDateRef = useRef(selectedDate);
    const hoveredIsoRef   = useRef(null);

    useEffect(() => { weatherDataRef.current = weatherData;  }, [weatherData]);
    useEffect(() => { selectedDateRef.current = selectedDate; }, [selectedDate]);

    // ── Load GeoJSON once ──────────────────────────────────────────────────────
    useEffect(() => {
        fetch('/data/geoBoundaries-KAZ-ADM1_simplified.geojson')
            .then(r => r.json())
            .then(setGeoData)
            .catch(console.error);
    }, []);

    // ── Load all 16 CSVs in parallel ──────────────────────────────────────────
    useEffect(() => {
        (async () => {
            const results = await Promise.all(
                Object.entries(REGION_MAP).map(async ([iso, { csv }]) => {
                    try {
                        const res  = await fetch(`/data/weather/${encodeURIComponent(csv)}`);
                        const text = await res.text();
                        return [iso, parseWeatherCSV(text)];
                    } catch (e) {
                        console.error('Не удалось загрузить:', csv, e);
                        return [iso, {}];
                    }
                })
            );
            setWeatherData(Object.fromEntries(results));
            setLoading(false);
        })();
    }, []);

    // ── Update layer fill colors when date or data changes ────────────────────
    useEffect(() => {
        const dateStr = selectedDate.format('YYYY-MM-DD');
        Object.entries(layerRefs.current).forEach(([iso, layer]) => {
            if (!layer) return;
            const d         = weatherData[iso]?.[dateStr];
            const isHovered = hoveredIsoRef.current === iso;
            layer.setStyle({
                fillColor: getTempColor(d?.tempMean),
                ...(isHovered ? HOVER_STYLE : NORMAL_STYLE),
            });
        });
    }, [selectedDate, weatherData]);

    // ── Stable GeoJSON style function (reads from refs on mount) ──────────────
    const featureStyle = useCallback((feature) => {
        const iso     = feature.properties.shapeISO;
        const dateStr = selectedDateRef.current.format('YYYY-MM-DD');
        const d       = weatherDataRef.current[iso]?.[dateStr];
        return { ...NORMAL_STYLE, fillColor: getTempColor(d?.tempMean) };
    }, []);

    // ── Per-feature Leaflet events (stable, use refs) ─────────────────────────
    const onEachFeature = useCallback((feature, layer) => {
        const iso = feature.properties.shapeISO;
        layerRefs.current[iso] = layer;

        layer.on({
            mouseover(e) {
                hoveredIsoRef.current = iso;
                const { clientX, clientY } = e.originalEvent;
                setTooltip({ x: clientX, y: clientY, iso });
                layer.setStyle({ ...HOVER_STYLE, fillColor: layer.options.fillColor });
                layer.bringToFront();
            },
            mousemove(e) {
                const { clientX, clientY } = e.originalEvent;
                setTooltip(prev => prev ? { ...prev, x: clientX, y: clientY } : null);
            },
            mouseout() {
                hoveredIsoRef.current = null;
                setTooltip(null);
                const dateStr = selectedDateRef.current.format('YYYY-MM-DD');
                const d       = weatherDataRef.current[iso]?.[dateStr];
                layer.setStyle({ ...NORMAL_STYLE, fillColor: getTempColor(d?.tempMean) });
            },
        });
    }, []);

    // ── Derived values ─────────────────────────────────────────────────────────
    const sliderValue   = selectedDate.diff(MIN_DATE, 'day');
    const tooltipRegion = tooltip?.iso ? t('weatherMap.region.' + tooltip.iso, REGION_MAP[tooltip.iso]?.name || tooltip.iso) : '';
    const tooltipData   = tooltip?.iso
        ? weatherData[tooltip.iso]?.[selectedDate.format('YYYY-MM-DD')]
        : null;

    return (
        <ConfigProvider locale={ruRU}>
            <div className={classes.page}>
                <Header />

                <main className={classes.container}>
                    <h1 className={classes.pageTitle}>{t('weatherMap.title')}</h1>

                    {/* ── Контролы ── */}
                    <div className={classes.controls}>
                        <Text className={classes.label}>{t('weatherMap.selectDate')}</Text>
                        <DatePicker
                            value={selectedDate}
                            onChange={d => d && setSelectedDate(d)}
                            minDate={MIN_DATE}
                            maxDate={MAX_DATE}
                            format="DD.MM.YYYY"
                            allowClear={false}
                            placeholder={t('date')}
                        />
                        <Text className={classes.selectedDateText}>
                            {t('weatherMap.selected')}&nbsp;<strong>{formatDateRu(selectedDate)}</strong>
                        </Text>
                    </div>

                    {loading ? (
                        <div className={classes.loadingBox}>
                            <Spin size="large" />
                            <Text className={classes.loadingText}>
                                {t('weatherMap.loadingData')}
                            </Text>
                        </div>
                    ) : (
                        <>
                            {/* ── Карта ── */}
                            <div className={classes.mapWrapper}>
                                <MapContainer
                                    center={[48.0, 68.0]}
                                    zoom={5}
                                    minZoom={4}
                                    maxZoom={8}
                                    className={classes.map}
                                    maxBounds={[[35.0, 40.0], [62.0, 96.0]]}
                                    maxBoundsViscosity={1.0}
                                >
                                    <TileLayer
                                        url="https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png"
                                        attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                                    />
                                    {geoData && (
                                        <GeoJSON
                                            key="kaz"
                                            data={geoData}
                                            style={featureStyle}
                                            onEachFeature={onEachFeature}
                                        />
                                    )}
                                </MapContainer>

                                {/* Цветовая легенда */}
                                <div className={classes.legend}>
                                    <span className={classes.legendTitle}>{t('weatherMap.meanTemperature')}</span>
                                    {legendItems.map(([color, label]) => (
                                        <div key={label} className={classes.legendItem}>
                                            <span className={classes.legendColor} style={{ background: color }} />
                                            <span>{label}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* ── Ползунок дат ── */}
                            <div className={classes.sliderSection}>
                                <Text className={classes.sliderLabel}>
                                    {t('weatherMap.observationPeriod')}
                                </Text>
                                <Slider
                                    min={0}
                                    max={TOTAL_DAYS}
                                    value={sliderValue}
                                    onChange={v => setSelectedDate(MIN_DATE.add(v, 'day'))}
                                    marks={SLIDER_MARKS}
                                    tooltip={{
                                        formatter: v => MIN_DATE.add(v, 'day').format('DD.MM.YYYY'),
                                    }}
                                    className={classes.slider}
                                />
                            </div>
                        </>
                    )}
                </main>

                <Footer />

                {/* ── Всплывающий тултип при наведении ── */}
                {tooltip && tooltipData && (
                    <div
                        className={classes.tooltip}
                        style={{ left: tooltip.x + 18, top: tooltip.y - 12 }}
                    >
                        <div className={classes.tooltipName}>{tooltipRegion}</div>
                        <div className={classes.tooltipDate}>{formatDateRu(selectedDate)}</div>
                        <div className={classes.tooltipGrid}>
                            <span>{t('weatherMap.meanTemperature')}</span>
                            <span>{fmt(tooltipData.tempMean, ' °C')}</span>

                            <span>{t('weatherMap.max')}</span>
                            <span>{fmt(tooltipData.tempMax, ' °C')}</span>

                            <span>{t('weatherMap.min')}</span>
                            <span>{fmt(tooltipData.tempMin, ' °C')}</span>

                            <span>{t('weatherMap.precipitation')}</span>
                            <span>{fmt(tooltipData.precipitation, ' мм')}</span>

                            <span>{t('weatherMap.rain')}</span>
                            <span>{fmt(tooltipData.rain, ' мм')}</span>

                            <span>{t('weatherMap.snow')}</span>
                            <span>{fmt(tooltipData.snowfall, ' см')}</span>

                            <span>{t('weatherMap.humidityMean')}</span>
                            <span>{fmt(tooltipData.humidityMean, '%')}</span>

                            <span>{t('weatherMap.humidityMaxMin')}</span>
                            <span>
                                {fmt(tooltipData.humidityMax, '%')} / {fmt(tooltipData.humidityMin, '%')}
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </ConfigProvider>
    );
}
